import { useId, useState } from 'react';
import { toast } from 'react-toastify';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import countryLabels from 'react-phone-number-input/locale/fr.json';
import 'react-phone-number-input/style.css';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import Modalheader from 'components/modal/components/header';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Button from 'components/button';
import StyledInput from 'components/styledInput/StyledInput';
import { WarningCircle } from 'assets/icons';
import {
  COMPANY_ADMIN_ROLE,
  companyRoleDescription,
  companyRoleOptions,
} from 'constants/companyRoles';
import { validateContactForm } from 'helper/companyContactForm';
import { toPhoneValue } from 'helper/companyContactPhone';

const OBSERVER_ROLE = '200';

// errorCode from the api → [field to highlight, text key under companyContacts]
const API_ERRORS = {
  EMAIL_INVALID: ['email', 'edit.errors.emailInvalid'],
  FIRSTNAME_INVALID: ['firstName', 'edit.errors.required'],
  LASTNAME_INVALID: ['lastName', 'edit.errors.required'],
  ROLE_INVALID: ['role', 'edit.errors.required'],
  ADMIN_ROLE_TAKEN: ['role', 'edit.errors.adminTakenUnknown'],
  PHONE_INVALID: ['phone', 'invite.errors.phoneInvalid'],
  ALREADY_MEMBER: ['email', 'invite.errors.alreadyMember'],
  TEMP_WORKER_ACCOUNT: ['email', 'invite.errors.tempWorker'],
};

const ModalAddCompanyMember = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn, language } = UseContext();
  const text = (key) => language.getValue(`companyContacts.${key}`, '');
  const t = (key) => text(`invite.${key}`);
  const fromHubspot = modalData?.origin === 'hubspot';
  // The modal is mounted anew on each opening: the initial state is enough to
  // start from the contact picked with « Inviter ».
  const prefill = modalData?.prefill ?? {};
  const [form, setForm] = useState({
    email: prefill.email ?? '',
    firstName: prefill.firstName ?? '',
    lastName: prefill.lastName ?? '',
    phone: toPhoneValue(prefill.phone) ?? '',
    role: OBSERVER_ROLE,
  });
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const phoneFieldId = useId();

  const roleList = companyRoleOptions(language).filter(
    (option) => Number(option.value) !== COMPANY_ADMIN_ROLE,
  );
  const inviteeName =
    [form.firstName.trim(), form.lastName.trim().toUpperCase()]
      .filter(Boolean)
      .join(' ') || t('someone');
  const description = t(
    fromHubspot ? 'hubspotDescription' : 'description',
  ).replace('{name}', () => inviteeName);
  const errorText = (key) => (key ? text(key) : undefined);

  const update = (field) => (eventOrValue) => {
    const value = eventOrValue?.target
      ? eventOrValue.target.value
      : (eventOrValue ?? '');
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const onSent = (data) => {
    setIsSending(false);
    if (!data?.user) {
      toast.error(text('edit.errors.generic'));
      return;
    }
    // A pending existing account is sent the invitation link, as a new one is;
    // only an active one is told it was attached, by a notice without a link.
    const wasInvited = !data.existingAccount || data.user.validation === 0;
    if (data.invitationSent === false) {
      toast.warning(t(wasInvited ? 'sentMailFailed' : 'attachedMailFailed'));
    } else {
      toast.success(t(wasInvited ? 'sent' : 'attached'));
    }
    setModalDataReturn({ type: 'employee_created', user: data.user });
    toggle();
  };

  const onFailed = (err) => {
    setIsSending(false);
    if (err?.name === 'CanceledError') return;
    const errorCode = err?.response?.data?.errorCode;
    if (errorCode === 'COMPANY_NOT_FOUND') {
      toast.error(t('errors.companyNotFound'));
      toggle();
      return;
    }
    const known = API_ERRORS[errorCode];
    if (known) {
      setErrors({ [known[0]]: known[1] });
      return;
    }
    toast.error(text('edit.errors.generic'));
  };

  const submit = () => {
    const clientErrors = Object.fromEntries(
      Object.entries(validateContactForm(form)).map(([field, key]) => [
        field,
        `edit.errors.${key}`,
      ]),
    );
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      clientErrors.phone = 'invite.errors.phoneInvalid';
    }
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }
    setIsSending(true);
    ApiDatabase.postAddCompanyMember(
      {
        idCompany: modalData.idCompany,
        employee: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          role: Number(form.role),
          ...(form.phone ? { phone: form.phone } : {}),
        },
        origin: fromHubspot ? 'hubspot' : 'manual',
        ...(fromHubspot && modalData.hubspotContactId
          ? { hubspotContactId: modalData.hubspotContactId }
          : {}),
      },
      onSent,
      onFailed,
    );
  };

  const roleDescription = companyRoleDescription(language, form.role);

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isSending}>
        {t('title')}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-4 text-sm'}>
          <p className={'text-sm text-gray-500'}>{description}</p>
          <StyledInput
            label={t('email')}
            type={'email'}
            value={form.email}
            onChange={update('email')}
            error={errorText(errors.email)}
          />
          <div className={'grid grid-cols-2 gap-3'}>
            <StyledInput
              label={t('firstName')}
              type={'text'}
              value={form.firstName}
              onChange={update('firstName')}
              error={errorText(errors.firstName)}
            />
            <StyledInput
              label={t('lastName')}
              type={'text'}
              value={form.lastName}
              onChange={update('lastName')}
              error={errorText(errors.lastName)}
            />
          </div>
          <div className={'flex flex-col gap-1 min-w-0 w-full'}>
            <label htmlFor={phoneFieldId} className={'ft-sm text-gray-500'}>
              {t('phone')}
            </label>
            <PhoneInput
              // `international` stays unset so that a pasted "+590…" is kept as
              // well as a national number (`international={false}` drops the
              // "+"); `initialValueFormat` shows a prefill in national format,
              // like a typed number, the flag naming its country.
              defaultCountry={'FR'}
              initialValueFormat={'national'}
              flags={flags}
              labels={countryLabels}
              value={form.phone || undefined}
              onChange={update('phone')}
              // 9.4 and 8.5 draw the flag inside the field's box: the border
              // goes on the library's wrapper, not on its input.
              className={
                'flex items-center gap-2 h-10 border rounded-md px-2 focus-within:border-yellow-500'
              }
              // The library derives these two from --PhoneInput-color--focus in
              // :root, where the value is already resolved: setting that
              // variable here would change nothing.
              style={{
                '--PhoneInputCountrySelectArrow-color--focus': '#ff9600',
                '--PhoneInputCountryFlag-borderColor--focus': '#ff9600',
              }}
              numberInputProps={{
                id: phoneFieldId,
                className:
                  'flex-1 min-w-0 h-full text-sm bg-transparent border-0 focus:outline-none',
              }}
            />
            {errors.phone && (
              <div className={'text-red-500 flex items-center gap-1 text-xs'}>
                <WarningCircle wh={16} color={'#EF4444'} />
                {errorText(errors.phone)}
              </div>
            )}
          </div>
          <div className={'flex flex-col gap-1'}>
            <StyledInput
              label={t('role')}
              type={'select'}
              value={form.role}
              list={roleList}
              onChange={update('role')}
              error={errorText(errors.role)}
            />
            {roleDescription && (
              <div className={'text-gray-500 ft-sm mt-2'}>
                {roleDescription}
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          size={'LG'}
          color={'SECONDARY'}
          disabled={isSending}
          onClick={toggle}
        >
          {t('cancel')}
        </Button>
        <Button
          size={'LG'}
          color={isSending ? 'DISABLED' : 'PRIMARY'}
          disabled={isSending}
          onClick={submit}
        >
          {isSending ? t('sending') : t('send')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalAddCompanyMember;
